import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { postListingQuestion, postListingAnswer } from '../../util/api';
import { Heading, PrimaryButton, SecondaryButton, NamedLink } from '../../components';

import css from './SectionListingQA.module.css';

/**
 * Nextmine: public Q&A on the listing page ("Ask a question").
 *
 * Short, PUBLIC questions and answers (not an open chat) - other buyers can see
 * the exchange. Contact info is filtered server-side (see contactInfoFilter), so
 * neither the buyer nor the seller can move the deal off Nextmine.
 *
 * - Anyone signed in (except the seller) can ask a question.
 * - Only the seller (isOwnListing) sees an answer box on unanswered questions.
 * - Questions live in the listing's publicData; the server returns the updated
 *   list after each post, so we render optimistically from local state.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing
 * @param {boolean} props.isOwnListing
 * @param {boolean} props.isAuthenticated
 * @param {Object} props.params - listing route params (for the login redirect)
 * @returns {JSX.Element}
 */
const SectionListingQA = props => {
  const intl = useIntl();
  const { listing, isOwnListing, isAuthenticated, params } = props;

  const listingId = listing?.id?.uuid;
  const initialQuestions = listing?.attributes?.publicData?.questions || [];

  const [questions, setQuestions] = useState(initialQuestions);
  const [questionText, setQuestionText] = useState('');
  const [askInProgress, setAskInProgress] = useState(false);
  const [askError, setAskError] = useState(null);

  // Seller's per-question answer drafts + state.
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [answerInProgressId, setAnswerInProgressId] = useState(null);
  const [answerError, setAnswerError] = useState({});

  const formatDate = iso =>
    iso ? intl.formatDate(new Date(iso), { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const handleAsk = () => {
    const text = questionText.trim();
    if (!text || !listingId) {
      return;
    }
    setAskInProgress(true);
    setAskError(null);
    postListingQuestion({ listingId, question: text })
      .then(data => {
        setQuestions(data.questions || []);
        setQuestionText('');
      })
      .catch(e => {
        setAskError(
          e?.blocked
            ? e.message
            : intl.formatMessage({ id: 'ListingPage.qaGenericError' })
        );
      })
      .finally(() => setAskInProgress(false));
  };

  const handleAnswer = questionId => {
    const text = (answerDrafts[questionId] || '').trim();
    if (!text || !listingId) {
      return;
    }
    setAnswerInProgressId(questionId);
    setAnswerError(prev => ({ ...prev, [questionId]: null }));
    postListingAnswer({ listingId, questionId, answer: text })
      .then(data => {
        setQuestions(data.questions || []);
        setAnswerDrafts(prev => ({ ...prev, [questionId]: '' }));
      })
      .catch(e => {
        setAnswerError(prev => ({
          ...prev,
          [questionId]: e?.blocked
            ? e.message
            : intl.formatMessage({ id: 'ListingPage.qaGenericError' }),
        }));
      })
      .finally(() => setAnswerInProgressId(null));
  };

  // Newest first.
  const ordered = [...questions].sort(
    (a, b) => new Date(b.askedAt).getTime() - new Date(a.askedAt).getTime()
  );

  return (
    <section className={css.root}>
      <Heading as="h2" rootClassName={css.heading}>
        <FormattedMessage id="ListingPage.qaTitle" values={{ count: questions.length }} />
      </Heading>
      <p className={css.intro}>
        <FormattedMessage id="ListingPage.qaIntro" />
      </p>

      {/* Ask box - signed-in buyers only (not the seller). */}
      {!isOwnListing ? (
        isAuthenticated ? (
          <div className={css.askBox}>
            <textarea
              className={css.textarea}
              value={questionText}
              maxLength={600}
              rows={3}
              placeholder={intl.formatMessage({ id: 'ListingPage.qaAskPlaceholder' })}
              onChange={e => setQuestionText(e.target.value)}
            />
            {askError ? <p className={css.error}>{askError}</p> : null}
            <PrimaryButton
              type="button"
              className={css.askButton}
              inProgress={askInProgress}
              disabled={!questionText.trim()}
              onClick={handleAsk}
            >
              <FormattedMessage id="ListingPage.qaAskButton" />
            </PrimaryButton>
          </div>
        ) : (
          <p className={css.signInPrompt}>
            <FormattedMessage
              id="ListingPage.qaSignInPrompt"
              values={{
                loginLink: (
                  <NamedLink
                    name="LoginPage"
                    to={{ state: { from: `/l/${params?.slug}/${params?.id}` } }}
                  >
                    <FormattedMessage id="ListingPage.qaSignInLink" />
                  </NamedLink>
                ),
              }}
            />
          </p>
        )
      ) : null}

      {ordered.length === 0 ? (
        <p className={css.empty}>
          <FormattedMessage id="ListingPage.qaEmpty" />
        </p>
      ) : (
        <ul className={css.list}>
          {ordered.map(q => (
            <li key={q.id} className={css.qaItem}>
              <div className={css.question}>
                <span className={css.qMark} aria-hidden="true">
                  Q
                </span>
                <div className={css.qBody}>
                  <p className={css.qText}>{q.question}</p>
                  <p className={css.meta}>
                    <FormattedMessage
                      id="ListingPage.qaAskedBy"
                      values={{ name: q.askerName, date: formatDate(q.askedAt) }}
                    />
                  </p>
                </div>
              </div>

              {q.answer ? (
                <div className={css.answer}>
                  <span className={css.aMark} aria-hidden="true">
                    A
                  </span>
                  <div className={css.aBody}>
                    <p className={css.aText}>{q.answer}</p>
                    <p className={css.meta}>
                      <FormattedMessage
                        id="ListingPage.qaAnsweredBySeller"
                        values={{ date: formatDate(q.answeredAt) }}
                      />
                    </p>
                  </div>
                </div>
              ) : isOwnListing ? (
                <div className={css.answerBox}>
                  <textarea
                    className={css.textarea}
                    value={answerDrafts[q.id] || ''}
                    maxLength={1200}
                    rows={3}
                    placeholder={intl.formatMessage({ id: 'ListingPage.qaAnswerPlaceholder' })}
                    onChange={e =>
                      setAnswerDrafts(prev => ({ ...prev, [q.id]: e.target.value }))
                    }
                  />
                  {answerError[q.id] ? <p className={css.error}>{answerError[q.id]}</p> : null}
                  <SecondaryButton
                    type="button"
                    className={css.answerButton}
                    inProgress={answerInProgressId === q.id}
                    disabled={!(answerDrafts[q.id] || '').trim()}
                    onClick={() => handleAnswer(q.id)}
                  >
                    <FormattedMessage id="ListingPage.qaAnswerButton" />
                  </SecondaryButton>
                </div>
              ) : (
                <p className={classNames(css.meta, css.awaiting)}>
                  <FormattedMessage id="ListingPage.qaAwaitingReply" />
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SectionListingQA;
